import type { CompanyProfile } from "@/utils/repositories/seo"

// Maximum number of unique job cities listed in the Organization `location`.
export const ORG_LOCATION_LIMIT = 20

// Social channels contributing to `sameAs`, in a stable order.
const SOCIAL_CHANNELS = ["linkedin", "xing", "instagram", "facebook", "youtube", "x"] as const

export interface OrganizationSchema {
  "@id": string
  "name": string
  "url": string
  "logo"?: string
  "numberOfEmployees"?: { "@type": "QuantitativeValue", "value": number }
  "location"?: Array<{ "@type": "Place", "name": string }>
  "sameAs"?: string[]
}

export interface OrganizationSchemaSources {
  jobShop: JobShop
  profile?: CompanyProfile | null
  cities?: string[]
}

/**
 * Extracts up to `limit` unique job cities from a Typesense offers response.
 * Reads each hit's `location` (a string array), trims, and de-duplicates
 * case-insensitively while preserving the response order (newest offers first).
 */
export function extractCities(response: TypesenseResponse | null | undefined, limit = ORG_LOCATION_LIMIT): string[] {
  const hits = response?.results?.[0]?.hits ?? []
  const seen = new Set<string>()
  const cities: string[] = []

  for (const hit of hits) {
    const locations = hit.document?.location
    if (!Array.isArray(locations))
      continue

    for (const raw of locations) {
      if (typeof raw !== "string")
        continue

      const city = raw.trim()
      if (!city)
        continue

      const key = city.toLowerCase()
      if (seen.has(key))
        continue

      seen.add(key)
      cities.push(city)
      if (cities.length >= limit)
        return cities
    }
  }

  return cities
}

/**
 * Builds the `sameAs` URLs for the Organization from the JobShop's social
 * channels plus the company website, trimmed and de-duplicated (case-insensitive).
 */
export function buildOrganizationSameAs(jobShop: JobShop, website?: string | null): string[] {
  const channels = jobShop["social-media"]?.config?.general?.socialChannels
  const candidates: Array<string | undefined | null> = [
    ...SOCIAL_CHANNELS.map(channel => channels?.[channel]?.url),
    website,
  ]

  const seen = new Set<string>()
  const urls: string[] = []
  for (const candidate of candidates) {
    const url = candidate?.trim()
    if (!url)
      continue
    const key = url.toLowerCase()
    if (seen.has(key))
      continue
    seen.add(key)
    urls.push(url)
  }
  return urls
}

/**
 * Assembles the site-wide Organization schema.org node from the JobShop
 * branding/settings, the KPI company profile and the job cities. Fields without
 * data are omitted so the node stays valid with partial data. `name` falls back
 * to the JobShop title when the company profile has none.
 */
export function buildOrganizationSchema({ jobShop, profile, cities }: OrganizationSchemaSources): OrganizationSchema {
  const url = jobShop.jobShopUrl
  const name = profile?.name?.trim() || jobShop.jobShopTitle
  const logo = jobShop.branding?.config?.general?.logo?.media?.url?.trim()
  const employees = profile?.employees
  const sameAs = buildOrganizationSameAs(jobShop, profile?.website)

  const organization: OrganizationSchema = {
    "@id": `${url}#organization`,
    name,
    url,
  }

  if (logo)
    organization.logo = logo
  if (typeof employees === "number" && employees > 0)
    organization.numberOfEmployees = { "@type": "QuantitativeValue", "value": employees }
  if (cities && cities.length > 0)
    organization.location = cities.map(city => ({ "@type": "Place", "name": city }))
  if (sameAs.length > 0)
    organization.sameAs = sameAs

  return organization
}
